import React, { useState, useEffect } from 'react';
import CompletedSessions from './CompletedSessions';
import OverallStats from './OverallStats';
import SessionsTable from './SessionsTable';
import './SessionsTabs.scss';
import { getAnalyzeActAPIUrl, getAPIUrl } from "../../api/apiUtils";
import { apiFetch } from "../../api/apiClient";

const API_BASE_URL = getAPIUrl();

const SessionsTabs = () => {
    const [activeTab, setActiveTab] = useState('stats');
    const [sessions, setSessions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [filters, setFilters] = useState({
        hours: 24,
        limit: 50,
        group_id: ''
    });
    const [showTooltip, setShowTooltip] = useState(null);

    const tabs = [
        { id: 'stats', label: 'Статистика', icon: 'fas fa-chart-bar' },
        { id: 'all', label: 'Все сессии', icon: 'fas fa-list' },
        { id: 'recent', label: 'Недавние', icon: 'fas fa-clock' },
        { id: 'active', label: 'Активные', icon: 'fas fa-play' },
        { id: 'completed', label: 'Завершенные', icon: 'fas fa-check' },
        { id: 'failed', label: 'Неудачные', icon: 'fas fa-times' },
        { id: 'groups', label: 'По группам', icon: 'fas fa-layer-group' }
    ];

    useEffect(() => {
        if (activeTab !== 'stats' && activeTab !== 'completed') {
            fetchSessions();
        }
    }, [activeTab, filters]);

    const fetchSessions = async (tab = activeTab) => {
        setLoading(true);
        setError(null);

        try {
            let url = `${API_BASE_URL}/monitoring/sessions`;

            switch (tab) {
                case 'recent':
                    url = `${API_BASE_URL}/monitoring/sessions/recent?hours=${filters.hours}`;
                    break;
                case 'active':
                    url = `${API_BASE_URL}/monitoring/sessions/active`;
                    break;
                case 'completed':
                    url = `${API_BASE_URL}/monitoring/sessions/completed?limit=${filters.limit}`;
                    break;
                case 'failed':
                    url = `${API_BASE_URL}/monitoring/sessions/failed?limit=${filters.limit}`;
                    break;
                case 'groups':
                    url = filters.group_id
                        ? `${API_BASE_URL}/monitoring/sessions/by-group?group_id=${filters.group_id}`
                        : `${API_BASE_URL}/monitoring/sessions/by-group`;
                    break;
                default:
                    url = `${API_BASE_URL}/monitoring/sessions`;
            }

            const response = await apiFetch(url);
            if (!response.ok) throw new Error('Ошибка загрузки данных');

            const data = await response.json();
            setSessions(data);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleFilterChange = (name, value) => {
        setFilters(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const renderFilters = () => {
        switch (activeTab) {
            case 'recent':
                return (
                    <div className="filter-row">
                        <label>За последние:</label>
                        <select
                            value={filters.hours}
                            onChange={(e) => handleFilterChange('hours', parseInt(e.target.value))}
                        >
                            <option value={1}>1 час</option>
                            <option value={6}>6 часов</option>
                            <option value={12}>12 часов</option>
                            <option value={24}>24 часа</option>
                            <option value={48}>48 часов</option>
                            <option value={168}>1 неделя</option>
                        </select>
                    </div>
                );
            case 'failed':
                return (
                    <div className="filter-row">
                        <label>Лимит:</label>
                        <select
                            value={filters.limit}
                            onChange={(e) => handleFilterChange('limit', parseInt(e.target.value))}
                        >
                            <option value={10}>10 записей</option>
                            <option value={25}>25 записей</option>
                            <option value={50}>50 записей</option>
                            <option value={100}>100 записей</option>
                            <option value={200}>200 записей</option>
                        </select>
                    </div>
                );
            case 'groups':
                return (
                    <div className="filter-row">
                        <label>Group ID:</label>
                        <input
                            type="text"
                            value={filters.group_id}
                            onChange={(e) => handleFilterChange('group_id', e.target.value)}
                            placeholder="Введите group_id для фильтрации"
                        />
                        <button onClick={fetchSessions}>Применить</button>
                    </div>
                );
            default:
                return null;
        }
    };

    const renderTable = () => {
        const isGroupStats = activeTab === 'groups' && !filters.group_id;
        const columns = isGroupStats
            ? ['group_id', 'group_name', 'total', 'completed', 'failed', 'running', 'pending']
            : ['session_id', 'job_type', 'group_id', 'group_name', 'meta_id', 'start_date', 'end_date', 'created_at', 'status'];

        return (
            <SessionsTable
                sessions={sessions}
                loading={loading}
                error={error}
                onRetry={fetchSessions}
                columns={columns}
                isGroupStats={isGroupStats}
            />
        );
    };

    return (
        <div className="sessions-tabs">
            {/* === Заголовок === */}
            <div className="sessions-tabs-header">
                <h2>
                    <i className="fas fa-chart-line"></i>
                    Детальный мониторинг
                </h2>
                <p className="sessions-tabs-subtitle">
                    Просмотр сессий в различных представлениях с фильтрацией и сортировкой
                </p>
            </div>

            {/* === Вкладки === */}
            <nav className="sessions-tab-nav">
                {tabs.map(tab => (
                    <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        className={`sessions-tab-btn ${activeTab === tab.id ? 'active' : ''}`}
                    >
                        <i className={tab.icon}></i>
                        {tab.label}
                    </button>
                ))}
            </nav>

            {/* === Фильтры === */}
            <div className="sessions-filters">
                {renderFilters()}
            </div>

            {/* === Контент === */}
            <div className={`sessions-tabs-content ${loading ? 'loading' : ''}`}>
                {error && (
                    <div className="sessions-error">
                        ⚠ {error}
                    </div>
                )}

                {activeTab === 'stats' && (
                    <div className="transition-opacity duration-300">
                        <OverallStats />
                    </div>
                )}

                {activeTab === 'completed' && (
                    <div className="transition-opacity duration-300">
                        <CompletedSessions />
                    </div>
                )}

                {activeTab !== 'stats' && activeTab !== 'completed' && (
                    <div className="transition-opacity duration-300">
                        {renderTable()}
                    </div>
                )}
            </div>
        </div>
    );
};

export default SessionsTabs;
